import { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { LoadingPage } from '../LoadingPage'
import * as S from './styles';
import * as G from '../../theme/global-styles';
import ClientLogo from '../../components/ClientLogo';
import PoweredByQITech from '../../components/PoweredByQITech';
import { ThemedButton } from '../../components/ThemedButton';
import { useThemeContext } from "../../contexts/ThemeContext";
import { ErrorPage } from '../ErrorPage';
import ComponentRenderer from '../../components/backend/ComponentRenderer';

export const ConditionalDocuments = ({
    signData,
    setSignerStep,
    setSignData,
}: {
    signData: any,
    setSignerStep: (step: string) => void,
    setSignData: (data: any) => void,
}) => {

    const { clientThemeSettings } = useThemeContext();

    const [error, setError] = useState({ has_error: false, status: null })
    const [loading, setLoading] = useState(false)
    const [sceneIndex, setSceneIndex] = useState(0);
    const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
    const contentEndRef = useRef<HTMLDivElement>(null)
    const scrollParentRef = useRef<HTMLElement | null>(null)

    const cascadeOnRejection: Boolean = signData?.cascade_on_rejection;

    useEffect(() => {
        const sentinel = contentEndRef.current;
        if (!sentinel) return;

        const findScrollableAncestor = (el: HTMLElement): HTMLElement => {
            let node = el.parentElement;
            while (node) {
                const { overflowY } = window.getComputedStyle(node);
                if (overflowY === 'auto' || overflowY === 'scroll') return node;
                node = node.parentElement;
            }
            return window as unknown as HTMLElement;
        };

        const scrollParent = findScrollableAncestor(sentinel);
        scrollParentRef.current = scrollParent;

        setHasScrolledToEnd(false);

        const THRESHOLD = 50;
        const checkScrollEnd = () => {
            const isWindow = (scrollParent as unknown) === window;
            const nearBottom = isWindow
                ? window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - THRESHOLD
                : scrollParent.scrollTop + scrollParent.clientHeight >= scrollParent.scrollHeight - THRESHOLD;
            if (nearBottom) setHasScrolledToEnd(true);
        };

        scrollParent.addEventListener('scroll', checkScrollEnd, { passive: true });
        checkScrollEnd();
        return () => scrollParent.removeEventListener('scroll', checkScrollEnd);
    }, [sceneIndex])
    const [checkboxState, setCheckboxState] = useState<Record<number, boolean>>({});
    const [decisions, setDecisions] = useState(() => {
        const len = signData.related_documents.length;
        const initialState: Record<number, any> = {};
        for (let i = 0; i < len; i++) {
            initialState[i] = null;
        }
        return initialState;
    });

    const handleComplete = async (finalDecisions: Record<number, boolean>) => {
        const payload = {
            step: "conditional_documents",
            event_date: new Date().toISOString(),
            documents: Object.entries(finalDecisions)
                .filter(([, decision]) => decision !== null)
                .map(([groupIndex, decision]) => ({
                    group_index: Number(groupIndex),
                    decision: decision ? "accepted" : "rejected",
                })),
        }

        const config = {
            headers: { Authorization: signData.signer_token },
        }

        setLoading(true);

        try {
            const response = await axios.patch('/signer', payload, config);
            setSignData({ ...signData, ...response.data });
            setSignerStep(response.data.next_step);
        } catch (error) {
            setError({
                has_error: true,
                status: [401, 403, 409].includes((error.response || {}).status)
                    ? error.response.status
                    : 500,
            });
        } finally {
            setLoading(false);
        }
    }

    const currentScene = useMemo(() => {
        return signData.related_documents[sceneIndex] ?? { content: [] };
    }, [sceneIndex, signData.related_documents])

    const acceptDisabled = useMemo(() => {
        return currentScene.content.some((item: any, index: number) =>
            item.component === 'checkbox' && item.required && !checkboxState[index]
        );
    }, [currentScene, checkboxState])

    const scrollToBottom = () => {
        const sp = scrollParentRef.current;
        if (sp && (sp as unknown) !== window) {
            sp.scrollTo({ top: sp.scrollHeight, behavior: 'smooth' });
        } else {
            contentEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const advanceScene = (updated: Record<number, boolean>) => {
        setCheckboxState({});
        if (sceneIndex < signData.related_documents.length - 1) {
            setSceneIndex(sceneIndex + 1);
            const sp = scrollParentRef.current;
            if (sp && (sp as unknown) !== window) {
                sp.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            handleComplete(updated);
        }
    }

    const handleReject = () => {
        if (!hasScrolledToEnd) { scrollToBottom(); return; }
        const updated = { ...decisions, [sceneIndex]: false };
        setDecisions(updated);
        if (cascadeOnRejection) {
            handleComplete(updated);
        } else {
            advanceScene(updated);
        }
    }

    const handleAccept = () => {
        if (!hasScrolledToEnd) { scrollToBottom(); return; }
        const updated = { ...decisions, [sceneIndex]: true };
        setDecisions(updated);
        advanceScene(updated);
    }

    if (loading) {
        return (
            <LoadingPage />
        )
    }

    return (
        <G.Container style={{padding: "16px 0"}}>
            <ClientLogo clientThemeSettings={clientThemeSettings} size="small" />
            <S.SceneContainer>
                <ComponentRenderer
                    components={currentScene.content}
                    clientThemeSettings={clientThemeSettings}
                    checkboxState={checkboxState}
                    onCheckboxChange={(index, checked) => setCheckboxState(prev => ({ ...prev, [index]: checked }))}
                />
                <div ref={contentEndRef} />
            </S.SceneContainer>
            <S.Footer>
                <S.ButtonContainer>
                    <ThemedButton
                        clientThemeSettings={clientThemeSettings}
                        loading={false}
                        onClick={handleReject}
                    >
                        {currentScene?.button_labels?.reject ?? 'Recusar'}
                    </ThemedButton>
                    <ThemedButton
                        clientThemeSettings={clientThemeSettings}
                        loading={false}
                        onClick={handleAccept}
                        disabled={acceptDisabled}
                    >
                        {currentScene?.button_labels?.accept ?? 'Aceitar'}
                    </ThemedButton>
                </S.ButtonContainer>
                <PoweredByQITech />
            </S.Footer>
            {error?.has_error &&
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep("login")}
                    resetError={() => setError({ has_error: false, status: null })}
                    clientThemeSettings={clientThemeSettings}
                />
            }
        </G.Container>
    );
};